import { RootState } from "store";
import { useEffect, ReactNode, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import Helpers from "commons/helpers";
import Strings from "constants/strings";
import Constants from "constants/index";
import SettingLayout from "./settingLayout";

import { useAuth } from "providers/authProvider";
import { ITitleRoute } from "commons/interfaces";
import { showLoading } from "store/slice/common.slice";
import { resetDataAlert } from "store/slice/common.slice";
import { updateTitleRoute } from "store/slice/titleRoute.slice";
import { Box, Button, Loading, SnackbarAlert, Typography } from "@maysoft/common-component-react";
import Screens from "constants/screens";


type variantButton = "text" | "contained" | "outlined" | "gradient";
type colorButton = "white" | "primary" | "secondary" | "info" | "success" | "warning" | "error" | "light" | "dark" | "default";
interface IProps {
    title?: string;
    route?: ITitleRoute[];
    children: ReactNode;

    // Cần có quyền mới hiện children
    isPermission: boolean;
    allowSettingLayout?: boolean;
    requiredOrganization?: boolean;

    listActions?: {
        title?: ReactNode;
        color?: colorButton;
        variant?: variantButton;
        onPress?: (data: any) => void;
    }[];
}

const DashboardLayout: React.FunctionComponent<IProps> = ({
    title,
    route,
    children,
    listActions,
    isPermission,
    allowSettingLayout,
    requiredOrganization,
}: IProps) => {

    const auth = useAuth();
    const navigate = useNavigate();
    const dispatchRedux = useDispatch();

    const user = useSelector((state: RootState) => state.user);
    const { dataAlert, loading } = useSelector((state: RootState) => state.common);

    useEffect(() => {
        const handleEventNotFound = () => {
            Helpers.showAlert(
                Strings.MESSAGE.NOT_FOUND,
                "error",
                () => { }
            );
            dispatchRedux(showLoading(false));
        }

        const handleNotDataPermission = () => {
            Helpers.showAlert(
                Strings.MESSAGE.NOT_DATA_PERMISSION,
                "error",
                () => { }
            );
            dispatchRedux(showLoading(false));
        };

        const handleNotPermission = () => {
            Helpers.showAlert(
                Strings.MESSAGE.NOT_PERMISSION,
                "error",
                async () => { await auth.signoutRedirect(); }
            );
            dispatchRedux(showLoading(false));
        };

        __EventEmitter.addListener(Constants.EventName.NOT_FOUND, handleEventNotFound);
        __EventEmitter.addListener(Constants.EventName.NOT_PERMISSION, handleNotPermission);
        __EventEmitter.addListener(Constants.EventName.NOT_DATA_PERMISSION, handleNotDataPermission);

        return () => {
            __EventEmitter.removeListener(Constants.EventName.NOT_FOUND, handleEventNotFound);
            __EventEmitter.removeListener(Constants.EventName.NOT_PERMISSION, handleNotPermission);
            __EventEmitter.removeListener(Constants.EventName.NOT_DATA_PERMISSION, handleNotDataPermission);
        }
    }, [navigate]);

    useEffect(() => {
        return () => { dispatchRedux(resetDataAlert()); };
    }, [dispatchRedux]);

    useEffect(() => {
        dispatchRedux(
            updateTitleRoute({
                route: route || [],
                titleScreen: title || "",
                targerScreen: `${window.location.pathname}${window.location.search}`,
            })
        );
        Helpers.setItemInLocalStorage(
            Constants.StorageKeys.FROM,
            `${window.location.pathname}${window.location.search}`
        );
    }, [title, route]);

    const renderContentView = () => {
        if (isPermission) {
            if (
                requiredOrganization &&
                (Helpers.isNullOrEmpty(user?.workingOrganization) || user?.workingOrganization === "0")
            ) {
                return <ConfirmOrganizationRequest onCreateOrg={() => { navigate(Screens.ORGANIZATION_CREATE); }} />
            } else {
                return (
                    <Box sx={{ px: { xs: 0, lg: "130px" } }}>
                        {[...listActions || []].length > 0 &&
                            <Box sx={{
                                mb: 2,
                                gap: 1,
                                display: "flex",
                                flexWrap: "wrap",
                                alignItems: "center",
                                justifyContent: "end",
                            }}>
                                {[...listActions || []].map((item, index) => (
                                    <Button
                                        key={index}
                                        color={item.color}
                                        onClick={item.onPress}
                                        variant={item.variant}
                                    >
                                        {item.title}
                                    </Button>
                                ))}
                            </Box>
                        }
                        {children}
                    </Box>
                );
            }
        } else {
            return (
                <Box sx={{
                    gap: 2,
                    display: "flex",
                    minHeight: "50vh",
                    alignItems: "center",
                    flexDirection: "column",
                    justifyContent: "center",
                }}>
                    <Box sx={{
                        display: "flex",
                        marginTop: "24px",
                        marginBottom: "24px",
                        alignItems: "center",
                        justifyContent: "center",
                    }}>
                        <Box sx={{
                            width: "99px",
                            height: "99px",
                            display: "flex",
                            textAlign: "center",
                            alignItems: "center",
                            justifyContent: "center",
                            border: "0.25em solid #facea8",
                            borderRadius: "50%",
                        }}>
                            <Box sx={{ color: "#facea8", fontSize: "3.75rem" }}>!</Box>
                        </Box>
                    </Box>
                    <Box sx={{ gap: 0.5, display: "grid", textAlign: "center" }}>
                        <Typography variant="h4" color="secondary">
                            {Strings.MESSAGE.NOT_PERMISSION}
                        </Typography>
                    </Box>
                </Box>
            );
        }
    };

    const autoHideDuration = useMemo(() => {
        if (dataAlert?.message?.length <= 50) {
            // 5 second
            return 5000;
        } else {
            // 10 second
            return 10000;
        }
    }, [dataAlert?.message])

    return (
        <>
            <Loading visible={loading} />
            <Box
                sx={({ breakpoints, transitions, functions: { pxToRem } }) => ({
                    px: 2,
                    py: 2,
                    position: "relative",
                    [breakpoints.up("xl")]: {
                        transition: transitions.create(["margin-left", "margin-right"], {
                            easing: transitions.easing.easeInOut,
                            duration: transitions.duration.standard,
                        }),
                    },
                })}
            >
                {allowSettingLayout && !Helpers.isNullOrEmpty(user?.userInfo?.userProfile?.id) &&
                    <SettingLayout
                        title={title}
                        route={route}
                        children={children}
                        listActions={listActions}
                    />
                }

                {!allowSettingLayout && !Helpers.isNullOrEmpty(user?.userInfo?.userProfile?.id) && renderContentView()}

                <SnackbarAlert
                    noStyling
                    autoHideDuration={autoHideDuration}
                    dataAlert={dataAlert}
                    onClose={() => {
                        dispatchRedux(resetDataAlert());
                    }}
                />
            </Box>
        </>
    );
};

export default DashboardLayout;


const ConfirmOrganizationRequest = ({
    onCreateOrg,
}: {
    onCreateOrg: () => void;
}) => {
    return (
        <Box
            sx={({ breakpoints, transitions }) => ({
                px: 2,
                py: 2,
                position: "relative",
                [breakpoints.up("xl")]: {
                    transition: transitions.create(["margin-left", "margin-right"], {
                        easing: transitions.easing.easeInOut,
                        duration: transitions.duration.standard,
                    }),
                },
            })}
        >
            <Box sx={{
                gap: 2,
                display: "flex",
                minHeight: "50vh",
                alignItems: "center",
                flexDirection: "column",
                justifyContent: "center",
            }}>
                <Box sx={{
                    display: "flex",
                    marginTop: "24px",
                    marginBottom: "24px",
                    alignItems: "center",
                    justifyContent: "center",
                }}>
                    <Box sx={{
                        width: "99px",
                        height: "99px",
                        display: "flex",
                        textAlign: "center",
                        alignItems: "center",
                        justifyContent: "center",
                        border: "0.25em solid #facea8",
                        borderRadius: "50%",
                    }}>
                        <Box sx={{
                            color: "#facea8",
                            fontSize: "3.75rem"
                        }}>
                            !
                        </Box>
                    </Box>
                </Box>

                <Box sx={{ gap: 0.5, display: "grid", textAlign: "center" }}>
                    <Typography variant="h6">{"Rất tiếc bạn chưa có tổ chức để truy cập vào hệ thống"}</Typography>
                </Box>

                <Typography variant="button">{"Vui lòng tạo tổ chức mới!"}</Typography>

                <Button variant="contained" color="info" onClick={onCreateOrg}>
                    {"Tạo tổ chức"}
                </Button>
            </Box>
        </Box>
    );
};
